import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import { AxiosError } from "axios";
import { Select, Spin } from "antd";
import { errorHandler } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, PlusIcon, TrashIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { useJsApiLoader } from "@react-google-maps/api";

const { Option } = Select;

const GOOGLE_LIBRARIES: ("places")[] = ["places"];

interface Director {
    name: string;
    contactNo: string;
    email: string;
    position: string;
}

interface FormValues {
    userid: string;
    name: string;
    nameGujarati: string;
    address: string;
    businessType: string;
    gstNo: string;
    mobileNo: string;
    whatsappNo: string;
    email: string;
    link: string;
    establishmentYear: string;
    description: string;
    visionmission: string;
    isactive: boolean;
}

const BusinessAdd: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
        libraries: GOOGLE_LIBRARIES,
    });
    const addressInputRef = useRef<HTMLInputElement | null>(null);

    const [loading, setLoading] = useState(false);

    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [userSearchValue, setUserSearchValue] = useState("");
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [firstUser, setFirstUser] = useState(0);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const fetchingUsersRef = useRef(false);
    const ownerOptionRef = useRef<any>(null);
    const rows = 10;

    const dedupeUsers = (list: any[]) =>
        list.filter((u, i, arr) => arr.findIndex(x => (x.id || x._id) === (u.id || u._id)) === i);

    const [directors, setDirectors] = useState<Director[]>([]);

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    const [certFiles, setCertFiles] = useState<File[]>([]);
    const [certPreviews, setCertPreviews] = useState<string[]>([]);
    const [existingCerts, setExistingCerts] = useState<{ filename: string; url: string }[]>([]);

    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const certInputRef = useRef<HTMLInputElement | null>(null);

    const formik = useFormik<FormValues>({
        initialValues: {
            userid: "",
            name: "",
            nameGujarati: "",
            address: "",
            businessType: "",
            gstNo: "",
            mobileNo: "",
            whatsappNo: "",
            email: "",
            link: "",
            establishmentYear: "",
            description: "",
            visionmission: "",
            isactive: true,
        },
        validationSchema: Yup.object({
            userid: Yup.string().required("Owner is required"),
            name: Yup.string().required("Business name is required"),
            nameGujarati: Yup.string().required("Gujarati name is required"),
            address: Yup.string().required("Address is required"),
            email: Yup.string().email("Invalid email"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const formData = new FormData();
                Object.entries(values).forEach(([key, value]) => {
                    formData.append(key, String(value));
                });
                formData.append("directors", JSON.stringify(directors.filter(d => d.name.trim())));

                if (logoFile) formData.append("logo", logoFile);
                certFiles.forEach(f => formData.append("certificateImages", f));

                if (id) {
                    formData.append("existingCertificates", JSON.stringify(existingCerts.map(c => c.filename)));
                    const response = await apiFunctions.put(`/business/admin/update/${id}`, formData, undefined, true);
                    if (response?.data?.success) {
                        showToast({ severity: "success", summary: "Updated", detail: "Business updated successfully", life: 3000 });
                        navigate("/admin/business");
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Update failed", life: 3000 });
                    }
                } else {
                    const response = await apiFunctions.post("/business/admin/create", formData, null, true);
                    if (response?.data?.success) {
                        showToast({ severity: "success", summary: "Created", detail: "Business created successfully", life: 3000 });
                        navigate("/admin/business");
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Create failed", life: 3000 });
                    }
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        },
    });

    const fetchUsers = async (reset = false) => {
        if (fetchingUsersRef.current) return;
        fetchingUsersRef.current = true;
        setFetchingUsers(true);
        const skip = reset ? 0 : firstUser;
        try {
            let url = `/user/dropdownlist/${skip}/${rows}`;
            if (userSearchValue && userSearchValue.length > 1) url += `/${userSearchValue}`;
            const response = await apiFunctions.get(url);
            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;
            if (reset) {
                setUserOptions(dedupeUsers([
                    ...(ownerOptionRef.current ? [ownerOptionRef.current] : []),
                    ...newItems
                ]));
                setFirstUser(rows);
            } else {
                setUserOptions(prev => dedupeUsers([...prev, ...newItems]));
                setFirstUser(prev => prev + rows);
            }
            setTotalUsersCount(total);
        } finally {
            fetchingUsersRef.current = false;
            setFetchingUsers(false);
        }
    };

    useEffect(() => {
        setFirstUser(0);
        fetchUsers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearchValue]);

    // Google Places autocomplete on Address field
    useEffect(() => {
        if (!isLoaded || !addressInputRef.current) return;
        const timer = setTimeout(() => {
            if (!addressInputRef.current) return;
            const autocomplete = new (window as any).google.maps.places.Autocomplete(
                addressInputRef.current
            );
            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (!place.place_id) return;
                formik.setFieldValue("address", place.formatted_address || place.name || "");
            });
        }, 100);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded]);

    const handleUserScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;
        if (scrollTop + offsetHeight >= scrollHeight - 10 && userOptions.length < totalUsersCount && !fetchingUsers) {
            fetchUsers();
        }
    };

    // Edit mode: load existing business
    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const response = await apiFunctions.get(`/business/admin/${id}`);
                if (response?.data?.success) {
                    const b = response.data.data;
                    formik.setValues({
                        userid: b.userid || "",
                        name: b.name || "",
                        nameGujarati: b.nameGujarati || "",
                        address: b.address || "",
                        businessType: b.businessType || "",
                        gstNo: b.gstNo || "",
                        mobileNo: b.mobileNo || "",
                        whatsappNo: b.whatsappNo || "",
                        email: b.email || "",
                        link: b.link || "",
                        establishmentYear: b.establishmentYear ? String(b.establishmentYear) : "",
                        description: b.description || "",
                        visionmission: b.visionmission || "",
                        isactive: !!b.isactive,
                    });
                    setDirectors((b.directors || []).map((d: any) => ({
                        name: d.name || "",
                        contactNo: d.contactNo || "",
                        email: d.email || "",
                        position: d.position || "",
                    })));
                    if (b.logoUrl) setLogoPreview(b.logoUrl);
                    setExistingCerts((b.certificateImages || []).map((f: string, i: number) => ({
                        filename: f,
                        url: (b.certificateImageUrls || [])[i] || "",
                    })));
                    // ensure owner appears in dropdown
                    if (b.userid && b.ownerName) {
                        ownerOptionRef.current = { id: b.userid, name: b.ownerName };
                        setUserOptions(prev =>
                            prev.some(u => (u.id || u._id) === b.userid)
                                ? prev
                                : [ownerOptionRef.current, ...prev]
                        );
                    }
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleCertChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setCertFiles(prev => [...prev, ...files]);
        setCertPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
    };

    const removeNewCert = (index: number) => {
        setCertFiles(prev => prev.filter((_, i) => i !== index));
        setCertPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingCert = (index: number) => {
        setExistingCerts(prev => prev.filter((_, i) => i !== index));
    };

    const addDirector = () => {
        setDirectors(prev => [...prev, { name: "", contactNo: "", email: "", position: "" }]);
    };

    const updateDirector = (index: number, field: keyof Director, value: string) => {
        setDirectors(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
    };

    const removeDirector = (index: number) => {
        setDirectors(prev => prev.filter((_, i) => i !== index));
    };

    const fieldError = (name: keyof FormValues) =>
        formik.touched[name] && formik.errors[name] ? (
            <small className="p-error block mt-1">{formik.errors[name] as string}</small>
        ) : null;

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate("/admin/business")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            <Card title={id ? "Edit Business" : "Add Business"}>
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-12 gap-4">

                        {/* Owner */}
                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Owner (User) *</label>
                            <Select
                                showSearch
                                allowClear
                                className="w-full h-[42px] mt-1"
                                placeholder="Select owner"
                                filterOption={false}
                                onSearch={(v) => setUserSearchValue(v)}
                                onPopupScroll={handleUserScroll}
                                notFoundContent={fetchingUsers ? <Spin size="small" /> : "No users"}
                                value={formik.values.userid || undefined}
                                onChange={(value) => formik.setFieldValue("userid", value || "")}
                            >
                                {userOptions.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name}{user.mobileno ? ` - ${user.mobileno}` : ""}
                                    </Option>
                                ))}
                            </Select>
                            {fieldError("userid")}
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Business Type</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.businessType}
                                onChange={(e) => formik.setFieldValue("businessType", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Name *</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.name}
                                onChange={(e) => formik.setFieldValue("name", e.target.value)} />
                            {fieldError("name")}
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Name (Gujarati) *</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.nameGujarati}
                                onChange={(e) => formik.setFieldValue("nameGujarati", e.target.value)} />
                            {fieldError("nameGujarati")}
                        </div>

                        <div className="col-span-12">
                            <label className="form-label">Address *</label>
                            <PrimeInputText
                                ref={addressInputRef as any}
                                className="w-full mt-1"
                                placeholder="Search address on Google Maps..."
                                value={formik.values.address}
                                onChange={(e) => formik.setFieldValue("address", e.target.value)}
                            />
                            {fieldError("address")}
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Mobile No</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.mobileNo}
                                onChange={(e) => formik.setFieldValue("mobileNo", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">WhatsApp No</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.whatsappNo}
                                onChange={(e) => formik.setFieldValue("whatsappNo", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Email</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.email}
                                onChange={(e) => formik.setFieldValue("email", e.target.value)} />
                            {fieldError("email")}
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">GST No</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.gstNo}
                                onChange={(e) => formik.setFieldValue("gstNo", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Establishment Year</label>
                            <PrimeInputText className="w-full mt-1" keyfilter="int" value={formik.values.establishmentYear}
                                onChange={(e) => formik.setFieldValue("establishmentYear", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Website / Link</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.link}
                                onChange={(e) => formik.setFieldValue("link", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Description</label>
                            <InputTextarea className="w-full mt-1" rows={3} value={formik.values.description}
                                onChange={(e) => formik.setFieldValue("description", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Vision & Mission</label>
                            <InputTextarea className="w-full mt-1" rows={3} value={formik.values.visionmission}
                                onChange={(e) => formik.setFieldValue("visionmission", e.target.value)} />
                        </div>

                        {/* Logo */}
                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Logo</label>
                            <div className="flex items-center gap-3 mt-1">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="logo" style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                ) : (
                                    <div style={{ width: "64px", height: "64px", borderRadius: "8px", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <BuildingOffice2Icon style={{ width: "28px", height: "28px", color: "#d1d5db" }} />
                                    </div>
                                )}
                                <Button type="button" label="Choose Logo" outlined onClick={() => logoInputRef.current?.click()} />
                                <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoChange} />
                            </div>
                        </div>

                        {/* Certificates */}
                        <div className="col-span-12">
                            <label className="form-label">Certificate Images</label>
                            <div className="flex items-center gap-3 flex-wrap mt-1">
                                {existingCerts.map((c, i) => (
                                    <div key={`ex-${i}`} style={{ position: "relative" }}>
                                        <img src={c.url} alt="certificate" style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                        <Button type="button" icon={<TrashIcon style={{ width: "14px", height: "14px" }} />} rounded severity="danger"
                                            style={{ position: "absolute", top: "-8px", right: "-8px", width: "24px", height: "24px", padding: 0 }}
                                            onClick={() => removeExistingCert(i)} />
                                    </div>
                                ))}
                                {certPreviews.map((p, i) => (
                                    <div key={`new-${i}`} style={{ position: "relative" }}>
                                        <img src={p} alt="certificate" style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                        <Button type="button" icon={<TrashIcon style={{ width: "14px", height: "14px" }} />} rounded severity="danger"
                                            style={{ position: "absolute", top: "-8px", right: "-8px", width: "24px", height: "24px", padding: 0 }}
                                            onClick={() => removeNewCert(i)} />
                                    </div>
                                ))}
                                <Button type="button" label="Add Images" outlined onClick={() => certInputRef.current?.click()} />
                                <input ref={certInputRef} type="file" accept="image/*" multiple hidden onChange={handleCertChange} />
                            </div>
                        </div>

                        {/* Directors */}
                        <div className="col-span-12">
                            <div className="flex items-center justify-between">
                                <label className="form-label">Directors</label>
                                <Button type="button" label="Add Director" icon={<PlusIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />} outlined size="small" onClick={addDirector} />
                            </div>
                            {directors.map((d, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
                                    <div className="col-span-12 lg:col-span-3">
                                        <PrimeInputText className="w-full" placeholder="Name" value={d.name}
                                            onChange={(e) => updateDirector(i, "name", e.target.value)} />
                                    </div>
                                    <div className="col-span-12 lg:col-span-3">
                                        <PrimeInputText className="w-full" placeholder="Contact No" value={d.contactNo}
                                            onChange={(e) => updateDirector(i, "contactNo", e.target.value)} />
                                    </div>
                                    <div className="col-span-12 lg:col-span-3">
                                        <PrimeInputText className="w-full" placeholder="Email" value={d.email}
                                            onChange={(e) => updateDirector(i, "email", e.target.value)} />
                                    </div>
                                    <div className="col-span-10 lg:col-span-2">
                                        <PrimeInputText className="w-full" placeholder="Position" value={d.position}
                                            onChange={(e) => updateDirector(i, "position", e.target.value)} />
                                    </div>
                                    <div className="col-span-2 lg:col-span-1">
                                        <Button type="button" icon={<TrashIcon style={{ width: "16px", height: "16px" }} />} severity="danger" outlined
                                            onClick={() => removeDirector(i)} />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Active */}
                        <div className="col-span-12 flex items-center gap-2">
                            <Checkbox inputId="isactive" checked={formik.values.isactive}
                                onChange={(e) => formik.setFieldValue("isactive", e.checked)} />
                            <label htmlFor="isactive" className="form-label m-0 cursor-pointer">Active</label>
                        </div>

                        <div className="col-span-12 flex justify-end gap-2 mt-2">
                            <Button type="button" label="Cancel" severity="secondary" onClick={() => navigate("/admin/business")} disabled={loading} />
                            <Button type="submit" label={id ? "Update" : "Create"} loading={loading} />
                        </div>

                    </div>
                </form>
            </Card>
        </div>
    );
};

export default BusinessAdd;
